<template>
  <h3 class="mb-6">
    Compras
  </h3>
  <v-row>
    <v-col cols="12">
      <data-table
        :headers="headers"
        :provider="fetchOrders"
        :pagination="pagination"
        :actions="actions"
        :filters="filters"
      >
        <template #[`item.order_number`]="{ item }">
          <span> {{ item?.order_number ?? "" }} </span>
        </template>
        <template #[`item.event.name`]="{ item }">
          <span> {{ item?.event?.title ?? "" }} </span>
        </template>
        <template #[`item.event.dates`]="{ item }">
          <span>
            {{
              fromSQL(item?.event?.dates?.start, 'dd/MM/yyyy HH:mm')
            }}
          </span>
        </template>
        <template #[`item.payment_method`]="{ item }">
          <span>
            {{ item.payment_method.name }}
          </span>
        </template>
        <template #[`item.total_value`]="{ item }">
          <span>
            {{ $formatters.currency(parseFloat(item.total_value)) }}
          </span>
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="statusColor(item.status)">
            {{ item.status }}
          </v-chip>
        </template>
        <template #[`item.created_at`]="{ item }">
          <span>
            {{
              $luxon.DateTime.fromSQL(item.created_at).toFormat(
                "dd/MM/yyyy HH:mm:ss"
              )
            }}</span>
        </template>
      </data-table>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import DataTable from '@/components/datatable/DataTable.vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { fromSQL } from '@/plugins/date'
import UserService from '@/services/UserService'
import { useSnackBar } from '@/stores/snackbar'
import { DataTableFilter, DataTableHeader, DataTablePagination, DefaultDataTableActions } from '@/types/DataTable'
import Order from '@/types/Order'
import { useAbility } from '@casl/vue'
import { ref, Ref } from 'vue'
import { useRoute } from 'vue-router'

const { can } = useAbility()
const route = useRoute()
const snackbar = useSnackBar()

const headers: DataTableHeader[] = [
  { title: 'Número da compra', value: 'order_number', key: 'order_number', sortable: false },
  { title: 'Evento', value: 'event.name', key: 'event.name', sortable: false },
  { title: 'Data do Evento', value: 'event.dates', key: 'event.dates', sortable: false, align: 'center' },
  { title: 'Forma de pagamento', value: 'payment_method.name', key: 'payment_method', sortable: false },
  { title: 'Valor Total', value: 'total_value', key: 'total_value', sortable: false, align: 'center' },
  { title: 'Ingressos', value: 'total_items', key: 'total_items', sortable: false, align: 'center' },
  { title: 'Status', value: 'status', key: 'status', sortable: false, align: 'center' },
  { title: 'Data da venda', value: 'created_at', key: 'created_at', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
]

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({ label: 'Número da Compra (IDV)', field: 'order_number', required: false }),
])

const pagination: Ref<DataTablePagination> = ref({
  page: 1,
  itemsPerPage: 5,
  total: 0,
  totalPages: 0,
})

const actions: DefaultDataTableActions<Order> = {
  read: {
    callback: (item: Order) => window.open(`/reports/orders/${item.id}`, '_blank'),
    condition: () => can('read', 'sales.report.orders'),
  },
}

function statusColor(status: string): string {
  const candidates: Record<string, string> = {
    'Confirmado': 'green',
    'Cancelado': 'red',
    'Recusado': 'orange',
    'Aguardando Pagamento': 'blue',
    'Cortesia': 'grey',
  }

  return candidates[status] ?? 'grey'
}

async function fetchOrders(page: number, filters: string, per_page?: number) {
  try {
    const response = await new UserService().orders(
      page,
      filters,
      per_page,
      route.params.id as string,
    )

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar as compras', 'error')

    throw error
  }
}

</script>
